import { useState, useRef, ChangeEventHandler, memo, useMemo, useEffect, MouseEventHandler } from 'react';
import { Input, Upload, UploadProps } from 'antd';
import CoverImage from '@/components/cover-image';
import UploadPicture from '@/components/upload-picture';
import LottieContainer from '@/components/lottie-container';
import { getGlobalStore, useGlobalStore } from '@/store/global';
import { createQaRecordId, toastInQa } from '@/utils/qa';
import { useQaStore } from '@/store/qa';
import { createNewQuestion } from '../../index.helper';
import { CameraSvg, SubjectSearchSvg, SendDisabledSvg, SendSvg, UploadPictureSvg } from '@/components/svg-icons/dialogue-tool';
import { ModalCloseSvg } from '@/components/svg-icons/common';
import { IMode } from './index.helper';
import classNames from 'classnames';
import './index.scss';

const DialogueTool = () => {
    const { publish } = useGlobalStore();
    const { qaConfig, pushQaRecords, validateCanSend, updateQaConfig, interruptAllExcute } = useQaStore();
    const {
        questionState = 0,
        lucaQuestionAgainNum = 0,
        teacherQuestionAgainNum = 0,
        appraiseState,
        transferLaborState,
        aiTag,
        lucaAskChatId,
        askId,
        showSearchQuestionsByOcrBtn,
        showCreateNewQaBtn,
        resourceType,
        isLucaAsk,
        subjectSearchOnOff,
        showSubjectSearchLabel,
    } = qaConfig;
    // 用户正在使用输入法
    const isComposing = useRef(false);
    const showTextareaStatus = useRef(false);
    const textAreaRef = useRef<HTMLTextAreaElement | null | any>(null);
    const [showTextarea, setShowTextarea] = useState(false);
    const [showStop, setShowStop] = useState(false);
    const [showContinue, setShowContinue] = useState(false);
    const [images, setImages] = useState<string[]>([]);
    const [value, setValue] = useState('');
    const [mode, setMode] = useState<IMode>('normal');

    const reset = () => {
        showTextareaStatus.current = false;
        setValue('');
        setImages([]);
        setShowStop(false);
        setShowContinue(false);
        setShowTextarea(false);
        setMode('normal');
    };

    // 上传文件格式校验
    const handleBeforeUpload: UploadProps['beforeUpload'] = file => {
        const types = ['image/png', 'image/jpg', 'image/jpeg'];
        if (types.includes(file.type)) return true;
        toastInQa('仅支持上传png/jpg/jpeg格式的图片');
        return false;
    };

    const handleAiUploadChange: UploadProps['customRequest'] = info => {
        const localUrl = URL.createObjectURL(info.file as File);
        getGlobalStore()?.publish('PictureCrop', { type: 'crop', url: localUrl });
    };

    const handleCanUpload: MouseEventHandler = event => {
        if (!validateCanSend()) {
            event.stopPropagation();
        }
    };

    const handleDeletePicture = (url: string) => {
        setImages(item => item.filter(item => item !== url));
    };

    const handleUploadChange = (urls: string[]) => {
        setImages(urls);
        textAreaRef.current!.focus();
    };

    const handlePreventHideInput: MouseEventHandler = event => {
        if (validateCanSend()) {
            showTextareaStatus.current = true;
        } else {
            event.stopPropagation();
        }
    };

    const handleShowTextarea = () => {
        showTextareaStatus.current = true;
        setShowTextarea(showTextareaStatus.current);
        setTimeout(() => textAreaRef.current.focus(), 1);
    };

    const handleBlur = () => {
        showTextareaStatus.current = false;
        setTimeout(() => setShowTextarea(showTextareaStatus.current), 500);
    };

    const handleTextareaChange: ChangeEventHandler<{ value: string }> = ev => {
        const content = ev.target.value;
        setValue(content);
    };

    const handleContinue = () => {
        if (!validateCanSend()) return;
        setShowContinue(false);
        updateQaConfig({ showWhetherSolve: false });
        pushQaRecords(
            { recordId: createQaRecordId('questioner'), role: 'questioner', type: 'basic', content: '请继续' },
            { filterRecordType: { retry: true, whetherSolve: true, goToTeacherQaTips: true, goToTeacherQa: true, guessYouAsk: true } }
        );
        publish('DialogueArea', { type: 'continue', content: '请继续' });
    };

    const handleStop = () => publish('DialogueArea', { type: 'stop' });

    const handleSend = (ev: any) => {
        if (ev.shiftKey || isComposing.current || !validateCanSend() || !value.trim()) return;
        ev?.preventDefault();
        // 取消创建大家都想问、相似问题、猜你想问卡片的请求
        interruptAllExcute('send_cancel');
        const recordId = createQaRecordId('questioner');
        pushQaRecords(
            { recordId, role: 'questioner', type: 'basic', content: value, pictures: images },
            {
                // 发送新消息, 清除 请重试、是否解决、可转名师提示、可转名师卡片、转名师权益不足（未成功转名师情况下）等记录
                filterRecordType: {
                    retry: true,
                    whetherSolve: true,
                    goToTeacherQaTips: true,
                    goToTeacherQa: true,
                    rechargeTeacherQa: transferLaborState !== 1,
                    guessYouAsk: true,
                },
            }
        );
        if (transferLaborState === 1) {
            updateQaConfig({ questionState: 0 });
            publish('DialogueArea', {
                type: isLucaAsk === 1 ? 'sendMessageToTeacher' : 'sendMessageToTeacherOfOldSystem',
                params: { recordType: 22, askId, images, text: { belongType: 0, content: value } },
                recordId,
            });
        } else if (aiTag === 1) {
            updateQaConfig({ showWhetherSolve: false });
            publish('DialogueArea', { type: 'luca', mode, content: value, images });
        } else {
            updateQaConfig({ showWhetherSolve: false });
            publish('DialogueArea', {
                type: subjectSearchOnOff ? 'subjectSearch' : 'searchSimilarQuestion',
                params: { recordType: subjectSearchOnOff ? 65 : 21, images, text: { belongType: 0, content: value } },
            });
        }
        reset();
    };

    useEffect(() => {
        getGlobalStore()?.subscribe('DialogueTool', (data: any) => {
            switch (data.type) {
                case 'showStop':
                    setShowStop(true);
                    break;
                case 'hideStop':
                    setShowStop(false);
                    break;
                case 'showContinue':
                    setShowContinue(true);
                    break;
                case 'echo':
                    setValue(data.content || '');
                    setImages(data.images || []);
                    setTimeout(() => {
                        const length = data.content?.length || 0;
                        textAreaRef.current?.focus();
                        textAreaRef.current?.resizableTextArea?.textArea?.setSelectionRange(length, length);
                    }, 1);
                    break;
                case 'focus':
                    handleShowTextarea();
                    break;
                default:
                    reset();
                    break;
            }
        });
    }, []);

    const showTool = useMemo(() => {
        if (
            (!transferLaborState && lucaAskChatId && lucaQuestionAgainNum <= 0) ||
            (transferLaborState === 1 && teacherQuestionAgainNum <= 0) ||
            questionState === 2 ||
            appraiseState === 1
        ) {
            return false;
        }
        return true;
    }, [qaConfig]);

    const Operation = useMemo(() => {
        if (!showSubjectSearchLabel && !showSearchQuestionsByOcrBtn && !showContinue && !showStop) return null;
        return (
            <div className="dialogue-tool-stream-controls">
                {showSearchQuestionsByOcrBtn && mode !== 'subject_search' && (
                    <Upload
                        accept=".jpg, .jpeg, .png"
                        showUploadList={false}
                        beforeUpload={handleBeforeUpload}
                        customRequest={handleAiUploadChange}
                    >
                        <div className="dialogue-tool-stream-controls__upload-picture" onClick={handleCanUpload}>
                            <CameraSvg className="dialogue-tool-stream-controls__upload-picture-icon" />
                            <span>拍题答疑</span>
                        </div>
                    </Upload>
                )}
                {showSubjectSearchLabel && mode !== 'subject_search' && (
                    <div
                        className="dialogue-tool-stream-controls__subject-search"
                        onClick={() => {
                            setMode('subject_search');
                            handleShowTextarea();
                        }}
                    >
                        <SubjectSearchSvg className="dialogue-tool-stream-controls__subject-search-icon" />
                        <span>学科搜索</span>
                    </div>
                )}
                {(showContinue || showStop) && <div style={{ flex: 1 }} />}
                {showContinue && (
                    <div className="dialogue-tool-stream-controls__continue" onClick={handleContinue}>
                        继续回答
                    </div>
                )}
                {showStop && (
                    <div className="dialogue-tool-stream-controls__stop" onClick={handleStop}>
                        停止回答
                    </div>
                )}
            </div>
        );
    }, [showStop, showContinue, showSearchQuestionsByOcrBtn, mode, showSubjectSearchLabel]);

    const Tool = useMemo(() => {
        return (
            <div className="dialogue-tool">
                {mode === 'subject_search' && (
                    <div className="dialogue-tool__mode">
                        <div className="dialogue-tool__subject-search-label">
                            <SubjectSearchSvg />
                            <span className="dialogue-tool__subject-search-label-text">学科搜索</span>
                        </div>
                        <ModalCloseSvg
                            className="dialogue-tool__mode-close"
                            onClick={() => {
                                setMode('normal');
                                !value && setShowTextarea(false);
                            }}
                        />
                    </div>
                )}
                {images.length > 0 && (
                    <div className="dialogue-tool-pictures">
                        {images.map(item => (
                            <CoverImage className="dialogue-tool-picture-contaier" key={item} url={item} allowPreview>
                                <div className="dialogue-tool-picture-delete" onClick={() => handleDeletePicture(item)} />
                            </CoverImage>
                        ))}
                    </div>
                )}
                {showTextarea || !!value ? (
                    <div className="dialogue-tool__textarea-container">
                        <Input.TextArea
                            ref={textAreaRef}
                            className="dialogue-tool__textarea"
                            autoSize={{ maxRows: 4 }}
                            value={value}
                            onBlur={handleBlur}
                            onChange={handleTextareaChange}
                            maxLength={2000}
                            onPressEnter={handleSend}
                            onCompositionStart={() => {
                                isComposing.current = true;
                            }}
                            onCompositionEnd={() => {
                                isComposing.current = false;
                            }}
                        />
                        <div className="dialogue-tool__operations">
                            {aiTag !== 1 || transferLaborState === 1 || resourceType === 2 ? (
                                <UploadPicture maxCount={6} pictures={images} onChange={handleUploadChange}>
                                    <UploadPictureSvg className="dialogue-tool__upload-picture" onClick={handlePreventHideInput} />
                                </UploadPicture>
                            ) : (
                                <div />
                            )}
                            {showStop ? (
                                <LottieContainer type="purpleBallBouncing" excuteType="autoPlayByInit" loop width={40} />
                            ) : value.trim() ? (
                                <SendSvg className="dialogue-tool__send" onClick={handleSend} />
                            ) : (
                                <SendDisabledSvg />
                            )}
                        </div>
                    </div>
                ) : questionState === 4 ? (
                    <div className={classNames('dialogue-tool__input-replacement', 'dialogue-tool__input-replacement--disabled')}>
                        暂不能发送消息（剩余 {teacherQuestionAgainNum} 次追问）
                    </div>
                ) : (
                    <div className="dialogue-tool__input-replacement" onClick={handleShowTextarea}>
                        {questionState === 5
                            ? `剩 ${teacherQuestionAgainNum} 次追问`
                            : lucaAskChatId
                            ? `剩 ${lucaQuestionAgainNum} 次追问`
                            : '请输入提问内容～'}
                    </div>
                )}
                {aiTag === 1 && <div className="dialogue-tool__ai-tips">AI可能会犯错误 请注意甄别重要信息</div>}
            </div>
        );
    }, [showTextarea, images, value, showStop, mode, qaConfig]);

    const SwitchControl = useMemo(() => {
        if (questionState === 2 || appraiseState === 1) {
            return (
                <>
                    {showCreateNewQaBtn && (
                        <div className="dialogue-tool-switch-controls">
                            <div className="dialogue-tool-switch-controls__new-question" onClick={createNewQuestion}>
                                新问题
                            </div>
                        </div>
                    )}
                    {aiTag === 1 && (
                        <div
                            className={classNames(
                                'dialogue-tool-switch-controls__ai-tips',
                                showCreateNewQaBtn && 'dialogue-tool-switch-controls__ai-tips--white-bg'
                            )}
                        >
                            <div>AI可能会犯错误 请注意甄别重要信息</div>
                        </div>
                    )}
                </>
            );
        }
        return null;
    }, [showCreateNewQaBtn, questionState, appraiseState, aiTag]);

    return (
        <>
            {(showTool || showStop) && Operation}
            {showTool && Tool}
            {SwitchControl}
        </>
    );
};

export default memo(DialogueTool);
